import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  AttributeGroupCard,
  isGroupLocked,
  labelForKey,
  type EnrichmentAttributeGroup,
  type EnrichmentRunPanelAction,
  type EnrichmentRunPanelState,
} from '~/components/common/enrichment-run-panel';
import { Button } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';

interface Props {
  groups: EnrichmentAttributeGroup[];
  state: EnrichmentRunPanelState;
  dispatch: (action: EnrichmentRunPanelAction) => void;
  selectedCount: number;
}

/**
 * Turns the Preview step's "N attributes selected" badge into a quick-edit
 * trigger: the same `AttributeGroupCard` list the Decision step renders, in a
 * popover, wired to the same `dispatch` — so an operator can flip a selection
 * without leaving Preview, and every downstream consumer (comparison rows,
 * strip tally, footer summary, stepper count) updates for free since they all
 * derive from this same reducer state.
 */
export function DeliveryWizardAttributePopover({ groups, state, dispatch, selectedCount }: Props) {
  const { t } = useTranslation('common');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-auto cursor-pointer gap-1 rounded-md border px-2.5 py-1 text-sm font-normal"
          data-testid="delivery-wizard-preview-attributes-popover-trigger"
        >
          <span className="font-semibold" data-testid="delivery-wizard-preview-attributes-value">
            {selectedCount}
          </span>{' '}
          {t('deliveryWizard.preview.badges.attributes')}
          <ChevronDown aria-hidden="true" className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="h-[350px] w-96 overflow-y-auto p-4"
        data-testid="delivery-wizard-preview-attributes-popover"
      >
        <h3 className="text-foreground text-sm font-semibold">
          {t('deliveryRun.attributes.title')}
        </h3>
        <p className="text-muted-foreground mb-3 text-xs">
          {t('deliveryRun.attributes.description')}
        </p>
        <div className="space-y-2.5" data-testid="delivery-wizard-preview-attributes-popover-list">
          {groups.map((group) => (
            <AttributeGroupCard
              key={group.slug}
              group={group}
              visibleAttributes={group.attributes}
              selected={state.selected}
              locked={isGroupLocked(state, group)}
              requiresLabel={
                group.requiresKey === undefined ? null : labelForKey(groups, group.requiresKey)
              }
              expanded={state.expanded[group.slug] !== false}
              labelForKey={(key) => labelForKey(groups, key)}
              onToggleGroup={() => {
                dispatch({ type: 'TOGGLE_GROUP', slug: group.slug });
              }}
              onToggleExpand={() => {
                dispatch({ type: 'TOGGLE_EXPAND', slug: group.slug });
              }}
              onToggleAttribute={(key) => {
                dispatch({ type: 'TOGGLE_ATTRIBUTE', key });
              }}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
